import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CreditCard, ArrowLeft, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useSatisfinderAuth } from '@/contexts/SatisfinderAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHODS = ['GCash', 'PayMaya', 'UPI', 'PayNow', 'IBAN'];
const CURRENCIES = ['PHP', 'SGD', 'INR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'AUD', 'JPY', 'MYR', 'IDR', 'THB', 'VND'];

const PLACEHOLDERS = {
  GCash:   'Registered mobile number (e.g. 09171234567)',
  PayMaya: 'Registered mobile number',
  UPI:     'UPI VPA (e.g. yourname@bank)',
  PayNow:  'Mobile number or NRIC',
  IBAN:    'Full IBAN (e.g. GB33BUKB20201555555555)',
};

const CURRENCY_DEFAULTS = { GCash: 'PHP', PayMaya: 'PHP', UPI: 'INR', PayNow: 'SGD', IBAN: 'GBP' };

/**
 * /satisfinder/payout — self-service payout details.
 * Full account_details is encrypted server-side; only masked_preview is
 * ever returned to the client.
 */
export default function SatisfinderPayout() {
  const { me, loading, refresh } = useSatisfinderAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [form, setForm] = useState({
    payout_method: 'GCash',
    account_holder_name: '',
    account_details: '',
    currency: 'PHP',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/satisfinder/payout/me`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setState(d);
          if (d.payout_method) {
            setForm(p => ({
              ...p,
              payout_method: d.payout_method,
              account_holder_name: d.account_holder_name || '',
              currency: d.currency || CURRENCY_DEFAULTS[d.payout_method] || 'PHP',
            }));
          }
        }
      } catch { /* ignored */ }
    })();
  }, []);

  useEffect(() => {
    if (me?.full_name && !form.account_holder_name) {
      setForm(p => ({ ...p, account_holder_name: me.full_name }));
    }
  }, [me]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setForm(p => ({ ...p, currency: CURRENCY_DEFAULTS[p.payout_method] || p.currency }));
  }, [form.payout_method]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] text-sm text-[#64748B]">Loading…</div>;
  if (!me) return <Navigate to="/satisfinder/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.account_details.trim()) { toast.error('Please enter your payment account details.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/satisfinder/payout`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Save failed'); return; }
      toast.success('Payout details saved securely.');
      setForm(p => ({ ...p, account_details: '' }));   // clear plaintext from memory
      await refresh();
      const r = await fetch(`${API}/satisfinder/payout/me`, { credentials: 'include' });
      if (r.ok) setState(await r.json());
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center">
          <Link to="/satisfinder" className="flex items-center gap-2">
            <img src="/satisfind-logo.png" alt="satisFIND" style={{ height: 36, width: 'auto' }} />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="sf-payout-page">
        <button onClick={() => nav('/satisfinder/dashboard')} className="text-sm text-[#475569] hover:text-[#0F172A] inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8">
          <div className="flex items-center gap-2 text-[#1A3E6F]">
            <CreditCard className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-widest font-bold">Payout Details</span>
          </div>
          <h1 className="font-['Montserrat'] text-2xl lg:text-3xl font-bold mt-2">Payout Details</h1>

          <p className="text-sm text-[#475569] mt-4 leading-relaxed">
            Add your payout details so satisFIND® can process approved rewards and reimbursements.
          </p>
          <div className="mt-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4 text-xs text-[#1E3A8A] leading-relaxed flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Your payout details are stored securely (encrypted at rest) and displayed in masked format on your profile.
              Only authorised satisFIND® team members can access payment information for payout processing.
            </span>
          </div>

          {state?.status === 'payout_method_added' && state.masked_preview && (
            <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-2" data-testid="payout-current">
              <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-700 mt-0.5" />
              <div>
                <p className="text-emerald-800 font-semibold">Current payout method on file</p>
                <p className="text-emerald-900 text-sm mt-1">
                  <span className="font-mono">{state.masked_preview}</span> · {state.account_holder_name} · {state.currency}
                </p>
                <p className="text-emerald-700 text-xs mt-1">You can update the details below at any time.</p>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Preferred payout method" required>
                <select value={form.payout_method} onChange={(e) => setForm(p => ({ ...p, payout_method: e.target.value }))} className={inputCls} data-testid="payout-method">
                  {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Currency" required>
                <select value={form.currency} onChange={(e) => setForm(p => ({ ...p, currency: e.target.value }))} className={inputCls} data-testid="payout-currency">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Account holder name" required>
              <input required value={form.account_holder_name} onChange={(e) => setForm(p => ({ ...p, account_holder_name: e.target.value }))} className={inputCls} data-testid="payout-holder" />
            </Field>
            <Field
              label="Payment account details" required
              helper={PLACEHOLDERS[form.payout_method]}
            >
              <input
                required autoComplete="off"
                value={form.account_details}
                onChange={(e) => setForm(p => ({ ...p, account_details: e.target.value }))}
                placeholder={PLACEHOLDERS[form.payout_method]}
                className={inputCls} data-testid="payout-details"
              />
            </Field>

            <button type="submit" disabled={busy} className="w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] text-white font-semibold py-3 rounded-lg text-sm mt-2" data-testid="payout-submit">
              {busy ? 'Saving securely…' : state?.status === 'payout_method_added' ? 'Update payout details' : 'Save payout details'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F] focus:border-[#1A3E6F] bg-white';

function Field({ label, required, helper, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#334155] mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {helper && <p className="text-[10.5px] text-[#64748B] mt-1 leading-snug">{helper}</p>}
    </div>
  );
}
